import { useCallback, useEffect, useRef } from 'react'
import { BuildCircle, Close, History, Star, StarBorder } from '../../../../primitives/index.js'
import {
  addToFavorites,
  addToRecent,
  removeFromFavorites,
  removeFromRecent,
  type SearchResultItemStory,
} from '../../../atoms/searchResults.js'
import { useChangeStory } from '../../../hooks/useChangeStory.js'
import classes from './search-story.module.css'

export type SearchStoryProps = {
  item: SearchResultItemStory
  variant?: 'recent' | 'favorite'
  onCloseSearchDialog: () => void
  navigationDisabled?: boolean
  active?: boolean
}

export function SearchStory(props: SearchStoryProps) {
  const { item, onCloseSearchDialog, variant, navigationDisabled, active } = props

  const itemRef = useRef<HTMLButtonElement | null>(null)

  const setActiveStory = useChangeStory()

  useEffect(() => {
    if (active) {
      const item = itemRef.current
      if (!item) return

      item.focus()
      item.scrollIntoView({ block: 'nearest' })
    }
  }, [active])

  const onClick = useCallback(() => {
    onCloseSearchDialog()
    if (variant !== 'favorite') {
      addToRecent(item)
    }
    setActiveStory(item.id)
  }, [item, onCloseSearchDialog, setActiveStory, variant])

  const onClickFav = useCallback(() => {
    addToFavorites(item)
  }, [item])

  const onClickRemove = useCallback(() => {
    removeFromRecent(item)
    removeFromFavorites(item)
  }, [item])

  const showFav = variant === 'recent'
  const hasVariant = !!variant
  const secondaryLabel = hasVariant ? item.path : undefined

  const icon = variant === 'recent' ? <History /> : variant === 'favorite' ? <Star /> : <BuildCircle />

  return (
    <div className={classes.listItem}>
      <button
        className={classes.listItemButton}
        disabled={navigationDisabled}
        onClick={onClick}
        ref={itemRef}
        type="button"
      >
        <div className={classes.listItemIcon}>{icon}</div>
        <div className={classes.listItemTextContainer}>
          <div className={classes.listItemTextPrimary}>{item.label}</div>
          <p className={classes.listItemTextSecondary}>{secondaryLabel}</p>
        </div>
      </button>

      {showFav && (
        <button className={classes.iconButton} onClick={onClickFav} type="button">
          <StarBorder />
        </button>
      )}

      {hasVariant && (
        <button className={classes.iconButton} onClick={onClickRemove} type="button">
          <Close />
        </button>
      )}
    </div>
  )
}
